import { cn } from "@web/lib/utils";
import { X } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

export type Toast = {
  id: number;
  title: string;
  description?: string;
  tone?: "info" | "warn" | "danger";
  ttl?: number;
};
let seq = 0;
let toasts: Toast[] = [];
const subs = new Set<() => void>();
const emit = () => {
  for (const s of subs) s();
};

export function toast(input: Omit<Toast, "id">): number {
  const id = ++seq;
  toasts = [...toasts, { id, ttl: 6000, tone: "info", ...input }];
  emit();
  return id;
}
export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function useToasts() {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => toasts,
    () => toasts,
  );
}

function ToastItem({ t }: { t: Toast }) {
  const { t: translate } = useTranslation();
  useEffect(() => {
    if (!t.ttl) return;
    const h = setTimeout(() => dismissToast(t.id), t.ttl);
    return () => clearTimeout(h);
  }, [t.id, t.ttl]);
  return (
    <output
      className={cn(
        "pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-md border bg-surface p-3 text-sm shadow-(--shadow-pop) animate-cedar-in",
        t.tone === "danger" ? "border-danger" : t.tone === "warn" ? "border-warn" : "border-line",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">{t.title}</p>
        {t.description ? <p className="mt-0.5 text-ink-2">{t.description}</p> : null}
      </div>
      <button
        type="button"
        onClick={() => dismissToast(t.id)}
        className="rounded-sm p-1 text-ink-3 hover:text-ink"
        aria-label={translate("actions.close")}
      >
        <X className="size-4" />
      </button>
    </output>
  );
}

export function Toaster() {
  const list = useToasts();
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2" aria-live="polite">
      {list.map((t) => (
        <ToastItem key={t.id} t={t} />
      ))}
    </div>
  );
}
